/**
 * ProjectMap — Interaktive Karte mit Projekt-Standorten.
 *
 * Lädt pro Projekt die Building-Data (Feld "ort"), geocodiert die Adresse
 * via Nominatim (OpenStreetMap) und zeigt Marker auf einer greyscale Karte.
 * Fixed unten links. Expand-Button vergrößert die Karte.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuthStore, type Project } from '../../store/auth'
import { apiFetch, projectUrl, API_URL } from '../../lib/apiBase'

declare global {
  interface Window {
    L: any
  }
}

// ── Leaflet loader (CDN) ────────────────────────────────────────────────────

let leafletLoaded = false
let leafletPromise: Promise<void> | null = null

function loadLeaflet(): Promise<void> {
  if (leafletLoaded) return Promise.resolve()
  if (leafletPromise) return leafletPromise

  leafletPromise = new Promise((resolve) => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
    document.head.appendChild(link)

    const script = document.createElement('script')
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
    script.onload = () => { leafletLoaded = true; resolve() }
    document.head.appendChild(script)
  })
  return leafletPromise
}

// ── Greyscale tile URL ──────────────────────────────────────────────────────

const TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png'
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'

// ── Geocoding via Nominatim ─────────────────────────────────────────────────

interface GeoResult {
  projectId: string
  name: string
  city: string
  street: string  // Straße + Hausnummer
  lat: number
  lon: number
}

interface GeocodeResult { lat: number; lon: number; city: string; street: string }

const geocodeCache: Record<string, GeocodeResult | null> = {}

async function geocodeAddress(address: string): Promise<GeocodeResult | null> {
  if (geocodeCache[address] !== undefined) return geocodeCache[address]
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&addressdetails=1&q=${encodeURIComponent(address)}`,
      { headers: { 'User-Agent': 'orto.space/1.0' } }
    )
    const data = await res.json()
    if (data.length > 0) {
      const d = data[0]
      const addr = d.address || {}
      const city = addr.city || addr.town || addr.village || addr.municipality || addr.county || ''
      const road = addr.road || ''
      const houseNr = addr.house_number || ''
      const street = road ? (houseNr ? `${road} ${houseNr}` : road) : ''
      const result: GeocodeResult = { lat: parseFloat(d.lat), lon: parseFloat(d.lon), city, street }
      geocodeCache[address] = result
      return result
    }
    geocodeCache[address] = null
    return null
  } catch {
    geocodeCache[address] = null
    return null
  }
}

/** Load building data for a project, extract "ort" field, geocode it */
async function loadProjectLocation(project: Project): Promise<GeoResult | null> {
  // If project already has coords, use them
  if (project.latitude != null && project.longitude != null) {
    return { projectId: project.id, name: project.name, city: project.address || '', street: '', lat: project.latitude, lon: project.longitude }
  }

  try {
    const res = await apiFetch(`${API_URL}/api/projects/${project.id}/building-data`)
    if (!res.ok) return null
    const json = await res.json()
    const bd = json.data || json
    const address = bd?.fields?.ort
    if (!address || typeof address !== 'string' || address.trim().length < 3) return null

    const geo = await geocodeAddress(address.trim())
    if (!geo) return null
    return { projectId: project.id, name: project.name, city: geo.city, street: geo.street, lat: geo.lat, lon: geo.lon }
  } catch {
    return null
  }
}

// ── Component ───────────────────────────────────────────────────────────────

export function ProjectMap() {
  const projects = useAuthStore(s => s.projects)
  const [expanded, setExpanded] = useState(false)
  const [visible, setVisible] = useState(true)
  const [ready, setReady] = useState(false)
  const [geoResults, setGeoResults] = useState<GeoResult[]>([])
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const markersRef = useRef<any[]>([])

  // Load Leaflet
  useEffect(() => {
    loadLeaflet().then(() => setReady(true))
  }, [])

  // Geocode all projects
  useEffect(() => {
    if (projects.length === 0) return
    let cancelled = false

    ;(async () => {
      // Geocode sequentially to respect Nominatim rate limit (1 req/s)
      const results: GeoResult[] = []
      for (const p of projects) {
        if (cancelled) break
        const geo = await loadProjectLocation(p)
        if (geo) {
          results.push(geo)
          // Update incrementally
          if (!cancelled) setGeoResults([...results])
        }
        // Rate limit: 1 request per second for Nominatim
        await new Promise(r => setTimeout(r, 1100))
      }
    })()

    return () => { cancelled = true }
  }, [projects])

  // Init map
  useEffect(() => {
    if (!ready || !mapContainerRef.current || mapRef.current) return
    const L = window.L
    if (!L) return

    const map = L.map(mapContainerRef.current, {
      zoomControl: false,
      attributionControl: false,
    }).setView([51.1657, 10.4515], 6)

    L.tileLayer(TILE_URL, {
      attribution: TILE_ATTR,
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map)

    L.control.zoom({ position: 'bottomright' }).addTo(map)

    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [ready])

  // Build label HTML based on zoom level
  const buildLabel = useCallback((g: GeoResult, zoom: number) => {
    const city = g.city || g.name
    if (zoom >= 10 && g.street) {
      return `<span class="ps-map__dot"></span><span class="ps-map__label">${city}<br/><span class="ps-map__street">${g.street}</span></span>`
    }
    return `<span class="ps-map__dot"></span><span class="ps-map__label">${city}</span>`
  }, [])

  // Refresh marker icons (called on zoom change)
  const refreshMarkerIcons = useCallback(() => {
    if (!mapRef.current || !window.L) return
    const L = window.L
    const zoom = mapRef.current.getZoom()
    markersRef.current.forEach((m, i) => {
      if (i < geoResults.length) {
        m.setIcon(L.divIcon({
          className: 'ps-map__poi',
          html: buildLabel(geoResults[i], zoom),
          iconSize: [0, 0],
          iconAnchor: [5, 5],
        }))
      }
    })
  }, [geoResults, buildLabel])

  // Update markers when geoResults change
  useEffect(() => {
    if (!mapRef.current) return
    const L = window.L
    const map = mapRef.current

    // Clear old markers
    markersRef.current.forEach(m => map.removeLayer(m))
    markersRef.current = []

    if (geoResults.length === 0) return

    const zoom = map.getZoom()

    for (const g of geoResults) {
      const icon = L.divIcon({
        className: 'ps-map__poi',
        html: buildLabel(g, zoom),
        iconSize: [0, 0],
        iconAnchor: [5, 5],
      })

      const marker = L.marker([g.lat, g.lon], { icon })
        .addTo(map)
        .bindTooltip(g.name, {
          className: 'ps-map__tooltip',
          direction: 'top',
          offset: [0, -12],
        })
      markersRef.current.push(marker)
    }

    // Fit bounds
    if (geoResults.length === 1) {
      map.setView([geoResults[0].lat, geoResults[0].lon], 14)
    } else {
      const bounds = L.latLngBounds(geoResults.map(g => [g.lat, g.lon]))
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 })
    }

    // Listen for zoom changes
    map.on('zoomend', refreshMarkerIcons)
    return () => { map.off('zoomend', refreshMarkerIcons) }
  }, [geoResults, ready, buildLabel, refreshMarkerIcons])

  // ── Fly to project on hover (via custom event) ──────────────────────────
  useEffect(() => {
    if (!mapRef.current || geoResults.length === 0) return
    const map = mapRef.current

    const onHover = (e: Event) => {
      const projectId = (e as CustomEvent).detail?.projectId
      if (!projectId) return
      const geo = geoResults.find(g => g.projectId === projectId)
      if (geo) {
        map.flyTo([geo.lat, geo.lon], Math.max(map.getZoom(), 12), { duration: 0.6 })
      }
    }

    window.addEventListener('ps:hover-project', onHover)
    return () => window.removeEventListener('ps:hover-project', onHover)
  }, [geoResults])

  // Invalidate size on expand/collapse
  const toggleExpand = useCallback(() => {
    setExpanded(e => !e)
    setTimeout(() => mapRef.current?.invalidateSize(), 350)
  }, [])

  if (!visible) return null

  return (
    <div className={`ps-map${expanded ? ' ps-map--expanded' : ''}`}>
      <div className="ps-map__container" ref={mapContainerRef} />

      <button className="ps-map__close" title="Karte schließen" onClick={() => setVisible(false)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <button className="ps-map__expand" title={expanded ? 'Verkleinern' : 'Vergrößern'} onClick={toggleExpand}>
        {expanded ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" />
            <line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        )}
      </button>
    </div>
  )
}
